// SoundManager — UI feedback sound for the "my love" PWA.
//
// Single voice: a warm high-G marimba ding.
//
//   MARIMBA (ding)
//     • Fundamental sine + a softer octave partial + a faint 4× harmonic
//       for clarity. NO sharp inharmonic transient — that's xylophone
//       territory; marimbas read as warmer / rounder because their tone
//       is closer to a pure pitch with mellow even harmonics.
//     • ~5 ms attack, ~0.65 s exponential decay.
//     • Tuned to G6 ≈ 1568 Hz (high G).
//     • Played on SEND, RECEIVE, and TRANSLATION events. No second
//       voice / glitter / shimmer layered on top — just the marimba.
//     • TRANSLATION plays a slightly higher pitch (≈ G#6) so the ear
//       can distinguish it from a fresh send / receive.
//
// IMPORTANT iOS contract:
//   We render the voice into an OfflineAudioContext → AudioBuffer →
//   16-bit PCM WAV blob, then play through HTMLAudioElement. This is
//   the only way to make iOS respect the physical ring/silent switch.
//   Web Audio API output plays REGARDLESS of the silent switch (because
//   iOS treats it as Solo Ambient / Playback session), whereas an
//   <audio> element honours the user's mute preference automatically.
//
// User-gesture rule:
//   iOS requires every HTMLAudioElement to be played at least once
//   inside a user gesture before it can be played from a passive
//   context (polling, push-received message, translation arrival).
//   `primeAudioOnFirstGesture()` installs a once-only document-level
//   listener that silently primes the element on the very first tap
//   anywhere in the app.

// Reference frequency: G6 = high G, the user's chosen key.
const G6 = 1567.98;
// Translation tone is one half-step up so it's perceptibly distinct
// from the send/receive ding without breaking out of the G major key.
const G_SHARP_6 = G6 * 1.0595; // ≈ G#6 ≈ 1661 Hz

let marimbaAudio = null;
let marimbaHigherAudio = null;
let initPromise = null;
let primedListenerInstalled = false;

// ---------- WAV encoding ----------
//
// Standard 16-bit PCM mono WAV. About 30 lines including header math.
const audioBufferToWavBlob = (buffer) => {
  const numChannels = 1; // we always render mono
  const sampleRate = buffer.sampleRate;
  const samples = buffer.getChannelData(0);
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = samples.length * bytesPerSample;
  const bufferSize = 44 + dataSize;

  const ab = new ArrayBuffer(bufferSize);
  const view = new DataView(ab);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i += 1) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // bits per sample
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  // Convert float [-1, 1] → int16, with simple clipping.
  let offset = 44;
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([view], { type: 'audio/wav' });
};

// ---------- Voice: marimba ----------
const renderMarimba = async (fundamental = G6, duration = 0.65) => {
  const sampleRate = 44100;
  const length = Math.floor(sampleRate * (duration + 0.05));
  const ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(
    1,
    length,
    sampleRate,
  );

  const t0 = 0;

  // --- Partial 1: fundamental — soft sine, this is the body of the tone.
  const osc1 = ctx.createOscillator();
  osc1.type = 'sine';
  osc1.frequency.value = fundamental;
  const g1 = ctx.createGain();
  g1.gain.setValueAtTime(0.0001, t0);
  // Softer attack than xylophone (~5 ms instead of <1 ms) — marimba
  // mallets are softer-tipped than xylophone mallets.
  g1.gain.exponentialRampToValueAtTime(0.7, t0 + 0.005);
  // Longer exponential tail (~650 ms) for warmth.
  g1.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
  osc1.connect(g1);
  g1.connect(ctx.destination);
  osc1.start(t0);
  osc1.stop(t0 + duration + 0.02);

  // --- Partial 2: octave above (2×) — adds warmth without the
  //                "ping" character of xylophone. Lower amplitude
  //                than the fundamental, and decays a bit faster.
  const osc2 = ctx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.value = fundamental * 2;
  const g2 = ctx.createGain();
  g2.gain.setValueAtTime(0.0001, t0);
  g2.gain.exponentialRampToValueAtTime(0.22, t0 + 0.006);
  g2.gain.exponentialRampToValueAtTime(0.001, t0 + duration * 0.55);
  osc2.connect(g2);
  g2.connect(ctx.destination);
  osc2.start(t0);
  osc2.stop(t0 + duration + 0.02);

  // --- Partial 3: 4× — very faint, just enough to brighten the attack
  //                without crossing into xylophone territory.
  const osc3 = ctx.createOscillator();
  osc3.type = 'sine';
  osc3.frequency.value = fundamental * 4;
  const g3 = ctx.createGain();
  g3.gain.setValueAtTime(0.0001, t0);
  g3.gain.exponentialRampToValueAtTime(0.06, t0 + 0.004);
  // Decays fastest — only present during the attack transient.
  g3.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
  osc3.connect(g3);
  g3.connect(ctx.destination);
  osc3.start(t0);
  osc3.stop(t0 + duration + 0.02);

  return ctx.startRendering();
};

// ---------- Public API ----------

// Lazy / idempotent initialiser. The OfflineAudioContext rendering happens
// once per page-load; afterwards we keep the WAV blob URLs around and just
// reset audio.currentTime + .play() on each trigger. Total bake time is
// usually < 60 ms on modern phones (single voice now).
const ensureReady = () => {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      if (typeof window === 'undefined') return;
      // Two rendered voices: the canonical G6 marimba (send / receive)
      // and the slightly higher G#6 marimba (translation only).
      const marimbaBuf = await renderMarimba(G6, 0.65);
      const marimbaHigherBuf = await renderMarimba(G_SHARP_6, 0.6);

      marimbaAudio = new Audio(URL.createObjectURL(audioBufferToWavBlob(marimbaBuf)));
      marimbaAudio.preload = 'auto';
      marimbaAudio.volume = 0.78;

      marimbaHigherAudio = new Audio(
        URL.createObjectURL(audioBufferToWavBlob(marimbaHigherBuf)),
      );
      marimbaHigherAudio.preload = 'auto';
      marimbaHigherAudio.volume = 0.72;
    } catch (_e) {
      // Audio synthesis is best-effort — never let a sound failure
      // crash the rest of the app.
      marimbaAudio = null;
      marimbaHigherAudio = null;
    }
  })();
  return initPromise;
};

// Prime BOTH audio elements during a user gesture so they can later be
// triggered from passive contexts (polling, push-received). Plays each
// element at volume 0 for one frame then pauses — invisible to the user,
// satisfies iOS's "must be played in a gesture once" rule.
//
// Safe to call multiple times — only installs its listener once and
// only runs the priming once.
export const primeAudioOnFirstGesture = () => {
  if (primedListenerInstalled) return;
  if (typeof document === 'undefined') return;
  primedListenerInstalled = true;

  const onFirstTap = async () => {
    try {
      await ensureReady();
      if (!marimbaAudio || !marimbaHigherAudio) return;
      const realVol1 = marimbaAudio.volume;
      const realVol2 = marimbaHigherAudio.volume;
      try {
        marimbaAudio.volume = 0;
        marimbaHigherAudio.volume = 0;
        await marimbaAudio.play();
        await marimbaHigherAudio.play();
        marimbaAudio.pause();
        marimbaHigherAudio.pause();
        marimbaAudio.currentTime = 0;
        marimbaHigherAudio.currentTime = 0;
      } finally {
        marimbaAudio.volume = realVol1;
        marimbaHigherAudio.volume = realVol2;
      }
    } catch (_e) {
      /* iOS might still reject — fail silent and rely on a real send/click. */
    }
  };

  // capture:true so we beat any stopPropagation handlers further down.
  document.addEventListener('click', onFirstTap, { once: true, capture: true });
  document.addEventListener('touchend', onFirstTap, { once: true, capture: true });
};

const triggerMarimba = async () => {
  await ensureReady();
  if (!marimbaAudio) return;
  try {
    marimbaAudio.currentTime = 0;
    await marimbaAudio.play();
  } catch (_e) {
    /* Browser blocked playback (iOS silent switch / no prior gesture). */
  }
};

const triggerMarimbaHigher = async () => {
  await ensureReady();
  if (!marimbaHigherAudio) return;
  try {
    marimbaHigherAudio.currentTime = 0;
    await marimbaHigherAudio.play();
  } catch (_e) {
    /* see triggerMarimba */
  }
};

export const playSent = () => {
  // Single marimba ding at G6 — no glitter / shimmer / second voice.
  triggerMarimba();
};

export const playReceived = () => {
  triggerMarimba();
};

export const playTranslation = () => {
  // Slightly higher pitch (G#6) so the user can ear-distinguish a
  // translation-arrived event from a fresh send / receive. Still a
  // marimba ding — same instrument, just one half-step up.
  triggerMarimbaHigher();
};
